import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

import { cx } from '../lib/class-names';

import alertStyles from './Alert.module.css';
import { Button } from './Button';
import styles from './SuccessNotification.module.css';

interface SuccessNotificationApi {
  notify: (message: string) => void;
  dismiss: () => void;
}

const SuccessNotificationContext = createContext<SuccessNotificationApi | null>(null);

/**
 * One success message that lives above the route tree.
 *
 * The polite live region is mounted on the first render, before any mutation,
 * so a screen reader is already listening when the text arrives. The visible
 * message is that same region: a second alert would announce it twice. The
 * host stays mounted across navigation. Errors stay on the page that produced
 * them.
 */
export function SuccessNotificationProvider({
  children,
}: {
  children: ReactNode;
}): React.JSX.Element {
  const [message, setMessage] = useState<string | null>(null);
  const notify = useCallback((next: string) => {
    setMessage(next);
  }, []);
  const dismiss = useCallback(() => {
    setMessage(null);
  }, []);
  const api = useMemo(() => ({ notify, dismiss }), [notify, dismiss]);

  const visible = message !== null;

  return (
    <SuccessNotificationContext.Provider value={api}>
      <div className={visible ? styles.host : styles.liveSlot}>
        <div className={visible ? styles.panel : undefined}>
          <div
            className={visible ? cx(alertStyles.alert, alertStyles.success) : 'visually-hidden'}
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            {message ?? ''}
          </div>
          {visible && (
            <Button type="button" variant="secondary" onClick={dismiss}>
              Dismiss
            </Button>
          )}
        </div>
      </div>
      {children}
    </SuccessNotificationContext.Provider>
  );
}

/** Publishes a success message only after the caller knows the mutation succeeded. */
export function useSuccessNotification(): SuccessNotificationApi {
  const api = useContext(SuccessNotificationContext);

  if (api === null) {
    throw new Error('useSuccessNotification must be used within AppProviders.');
  }

  return api;
}
